import { useEffect, useMemo, useRef, useState } from "react";

// Live driver position for a ride.
// The backend provides the driver's reported position (driver.lat/lng) plus pickup/drop and status.
// Between polls we move the marker smoothly toward the current target so the map feels alive;
// whenever the backend reports a new position we snap to it.
const APPROACH_SECONDS = 45;

const lerp = (a, b, t) => a + (b - a) * t;
const ease = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

export function haversineKm(a, b) {
  if (!a || !b) return 0;
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export default function useDriverPosition(ride) {
  const status = ride?.status;
  const backendPos = ride?.driver && Number.isFinite(ride.driver.lat) ? { lat: ride.driver.lat, lng: ride.driver.lng } : null;
  const pickup = ride ? { lat: ride.pickup_lat, lng: ride.pickup_lng } : null;
  const drop = ride ? { lat: ride.drop_lat, lng: ride.drop_lng } : null;

  const segment = useMemo(() => {
    if (!ride) return null;
    if (status === "driver_assigned" || status === "driver_arriving" || status === "searching") return { from: backendPos || pickup, to: pickup, seconds: APPROACH_SECONDS, label: "to pickup" };
    if (status === "in_progress") return { from: pickup, to: drop, seconds: Math.min(150, Math.max(30, (ride.duration_min || 5) * 8)), label: "to destination" };
    if (status === "completed") return { from: drop, to: drop, seconds: 1, label: "arrived" };
    return null;
  }, [ride?.ride_id, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const [pos, setPos] = useState(backendPos || pickup);
  const [progress, setProgress] = useState(0);
  const startRef = useRef(Date.now());
  const fromRef = useRef(segment?.from);
  const lastBackend = useRef(backendPos ? `${backendPos.lat},${backendPos.lng}` : "");

  // New segment (status change): start from where the marker currently is
  useEffect(() => {
    if (!segment) return;
    fromRef.current = pos || segment.from;
    if (status === "in_progress") fromRef.current = pickup; // trip always starts at pickup
    startRef.current = Date.now();
    setProgress(0);
  }, [segment]); // eslint-disable-line react-hooks/exhaustive-deps

  // Backend reported a genuinely new position: snap to it
  useEffect(() => {
    if (!backendPos) return;
    const key = `${backendPos.lat},${backendPos.lng}`;
    if (key !== lastBackend.current) {
      lastBackend.current = key;
      fromRef.current = backendPos;
      startRef.current = Date.now();
      setPos(backendPos);
    }
  }, [backendPos?.lat, backendPos?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  // Animate
  useEffect(() => {
    if (!segment || !segment.to || !fromRef.current) return undefined;
    const tick = () => {
      const t = Math.min(1, (Date.now() - startRef.current) / (segment.seconds * 1000));
      const e = ease(t);
      const from = fromRef.current;
      setPos({ lat: lerp(from.lat, segment.to.lat, e), lng: lerp(from.lng, segment.to.lng, e) });
      setProgress(t);
    };
    tick();
    const id = setInterval(tick, 700);
    return () => clearInterval(id);
  }, [segment]);

  const remainingKm = segment?.to && pos ? haversineKm(pos, segment.to) : 0;
  const remainingMin = Math.max(0, Math.round((segment?.seconds || 0) * (1 - progress) / 60 * 10) / 10);

  return { pos, progress, segment, remainingKm, remainingMin };
}
